import { useMutation, useQueryClient } from 'react-query';
import { toast } from 'sonner';

import { APIBaseChronos } from '../hosts';
import { QueryEntity } from '../types';
import useGetFetchConfig, { FetchMethod } from '../useGetFetchConfig';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { Fact } from '@/types';

interface UseSetKeyForSubsetFactsParams {
  key: boolean;
  factIds: string[];
}

export const useSetKeyForSubsetFacts = (matterId: string, subsetId: string) => {
  const { getFetchConfig } = useGetFetchConfig();
  const queryClient = useQueryClient();

  return useMutation(
    async ({ key, factIds }: UseSetKeyForSubsetFactsParams) => {
      const fetchConfig = getFetchConfig({
        method: FetchMethod.PATCH,
        data: { key, factIds },
      });

      trackCustomEvent(TrackingEventsV2.FactSetKey);

      const response = await fetch(
        `${APIBaseChronos}/api/matter/${matterId}/subset/${subsetId}/facts/set-key`,
        fetchConfig,
      );

      if (!response.ok) {
        throw new Error('Failed to update key facts');
      }

      return response;
    },
    {
      onMutate: async ({ key, factIds }: UseSetKeyForSubsetFactsParams) => {
        // Cancel any outgoing refetches to avoid race conditions
        await queryClient.cancelQueries({ queryKey: [QueryEntity.SubsetFacts, { subsetId }] });

        // Snapshot the previous value
        const previousSubsetFacts = queryClient.getQueryData([QueryEntity.SubsetFacts, { subsetId }]);

        // Optimistically update the facts
        queryClient.setQueryData<{ facts: Fact[] }>([QueryEntity.SubsetFacts, { subsetId }], (old) => {
          if (!old?.facts) return old as { facts: Fact[] };

          return {
            ...old,
            facts: old.facts.map((fact) => {
              if (factIds.includes(fact.id)) {
                return { ...fact, isKey: key };
              }
              return fact;
            }),
          };
        });

        // Return context with the snapshot for rollback
        return { previousSubsetFacts };
      },
      onSuccess: (_, { key }) => {
        // Invalidate to ensure data consistency (will refetch in background)
        queryClient.invalidateQueries({ queryKey: [QueryEntity.SubsetFacts, { subsetId }] });
        queryClient.invalidateQueries({ queryKey: [QueryEntity.SubsetFactAllIds, { subsetId }] });

        const action = key ? 'added to' : 'removed from';
        toast.success(`Successfully ${action} key facts`);
      },
      onError: (error, _, context) => {
        // Rollback on error
        if (context?.previousSubsetFacts) {
          queryClient.setQueryData([QueryEntity.SubsetFacts, { subsetId }], context.previousSubsetFacts);
        }

        toast.error('Failed to update key facts');
      },
    },
  );
};
